export const ROOT_IMG_URL = '/react-templates/templates/2025_TCHIP/build/images/';

/**
 * Décision 2 du plan de passation : le chrome (header, frise, menu, modale de
 * recherche, footer) vient de WordPress via <wp-header> / <wp-footer>.
 * Repasser ce drapeau à `false` rend le chrome React ISO écrit à côté
 * (`ui/chrome/`), qui reste la solution de repli si le plugin tombe.
 *
 * ⚠️ Ne couvre **que** le chrome. Les blocs éditoriaux du listing ont leur
 * propre drapeau ci-dessous : les deux chantiers WordPress n'arriveront pas
 * ensemble (le chrome demande en plus du CSS et la réserve d'espace décrite en
 * décision 10, toujours à écrire), et les mélanger voudrait dire casser le haut
 * de page le jour où les widgets de contenu sont prêts.
 */
export const USE_WP_CHROME = true;

/**
 * Blocs éditoriaux du listing servis par le plugin WordPress — « bons plans »
 * (spec 2.c), « tarifs » (2.d) et la colonne d'avis de la section note (2.e).
 *
 * Le retour Placeloop a tranché : il n'existe pas de contenu éditable sur la
 * page de résultats du store locator, ces trois blocs passent donc par des
 * `<wp-widget>`. Tant que le drapeau est à `false`, on rend les composants React
 * alimentés par `staticContent.js` — ils montrent le rendu attendu et servent de
 * spec HTML/CSS à recopier dans les widgets WordPress (§9 du plan).
 *
 * Les slugs attendus côté WordPress : `bons-plans`, `tarifs`, `avis`.
 */
export const USE_WP_WIDGETS = true;

/**
 * Site WordPress qui sert le chrome et les widgets.
 *
 * ⚠️ Preprod pour l'instant : le plugin « Theme Embed Widgets » n'est PAS
 * installé sur tchip.fr (vérifié — `assets/embed.js` y répond 404), il ne l'est
 * que sur cette preprod. Conséquence à connaître : c'est le `site_url` renvoyé
 * par l'API qui pilote `rewriteUrls()` dans embed.js, donc **tous les liens du
 * header et du footer pointent sur la preprod**. Bascule sur `https://tchip.fr`
 * dès que le plugin y est déployé — c'est la seule ligne à changer.
 */
export const WP_SITE_URL = 'https://tchip.fr';

/** Script du plugin qui définit <wp-header>, <wp-footer> et <wp-widget>. */
export const WP_EMBED_SCRIPT_URL = `${WP_SITE_URL}/wp-content/plugins/theme-embed-widgets/assets/embed.js`;

/**
 * `embed.js` sert les deux chantiers : il définit aussi bien `<wp-header>` /
 * `<wp-footer>` que `<wp-widget>`. Il faut donc le charger dès que **l'un** des
 * deux drapeaux est actif — les activer séparément est prévu (cf. l'avertissement
 * sur `USE_WP_CHROME`).
 */
export const USE_WP_EMBED = USE_WP_CHROME || USE_WP_WIDGETS;

/**
 * Évènement émis par embed.js sur `document` une fois tous les scripts du
 * thème chargés (`loadScripts`). C'est le seul point d'accroche fiable pour
 * repasser derrière `main.js` du thème, qui pose ses globales en fin d'IIFE.
 */
export const WP_SCRIPTS_LOADED_EVENT = 'wp-embed-scripts-loaded';

/** Évènement que le header WordPress doit émettre pour ouvrir la modale de recherche. */
export const OPEN_SEARCH_EVENT = 'tchip:open-search';

/** File d'attente — URL unique pour tous les salons (cf. §18 du plan). */
export const FILE_ATTENTE_URL = 'https://filedattentetchip.fr/home';

/**
 * Hauteurs par défaut du chrome (décision 10).
 *
 * ⚠️ Constante inutilisée, et elle peut le rester : la réserve d'espace du
 * chrome WordPress est assurée statiquement par le CSS critique inline de
 * `public/index.html` (`--header-h` / `--frise-h` + media queries), pas par du
 * JavaScript — l'observateur qui réécrivait ces variables cassait le rendu,
 * voir l'en-tête de `ui/chrome/SiteHeader.js`.
 *
 * Ces valeurs ont été confrontées au thème WordPress à l'activation du chrome :
 * `tchip-theme` déclare exactement les mêmes tokens et les mêmes valeurs
 * (80px / 56px), et son propre bloc `:root` est neutralisé par le préfixage de
 * scope du plugin — le chrome WP résout donc `--header-h` / `--frise-h` depuis
 * le `:root` de `styles/base.css`. La réserve statique est donc juste.
 */
export const CHROME_DEFAULTS = { headerHeight: 80, friseHeight: 56 };
